import React from 'react';
import styled from 'styled-components';
import { FiSearch, FiFilter, FiX } from 'react-icons/fi';
import theme from '../../styles/theme';

const FiltrosContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
`;

const FiltrosHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[6]};
`;

const Title = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FiltrosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FiltroGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

// Lista de segmentos disponíveis para filtro
const SEGMENTOS_OPTIONS = [
  { value: 'Revenda Veículos', label: 'Revenda Veículos' },
  { value: 'Concessionária', label: 'Concessionária' },
  { value: 'Loja de Motos', label: 'Loja de Motos' },
  { value: 'Locadora', label: 'Locadora' },
  { value: 'Oficina Mecânica', label: 'Oficina Mecânica' },
  { value: 'Funilaria', label: 'Funilaria' },
  { value: 'Autopeças', label: 'Autopeças' },
  { value: 'Despachante', label: 'Despachante' },
  { value: 'Seguradora', label: 'Seguradora' },
  { value: 'Financeira', label: 'Financeira' },
  { value: 'Outro', label: 'Outro' },
];

const LeadsFiltros = ({ 
  filtros, 
  onFiltrosChange, 
  onBuscar, 
  loading, 
  funisList = [], 
  origensList = [] 
}) => {
  const handleInputChange = (field, value) => {
    onFiltrosChange({
      ...filtros,
      [field]: value
    });
  };

  const limparFiltros = () => {
    const filtrosLimpos = {
      search: '',
      funil_id: '',
      origem_id: '',
      empresa: '',
      segmento: '',
      page: 1,
      limit: filtros.limit || 10
    };
    onFiltrosChange(filtrosLimpos);
    if (onBuscar) {
      onBuscar(filtrosLimpos);
    }
  };

  const temFiltrosAtivos = Object.entries(filtros).some(([key, value]) => {
    if (key === 'page' || key === 'limit') return false;
    return value !== '';
  });

  return (
    <FiltrosContainer>
      <FiltrosHeader>
        <Title>
          <FiFilter />
          Filtros de Busca
        </Title>
        <Actions>
          <ActionButton onClick={limparFiltros} disabled={!temFiltrosAtivos}>
            <FiX />
            Limpar Filtros
          </ActionButton>
          <ActionButton onClick={onBuscar} disabled={loading}>
            <FiSearch />
            {loading ? 'Buscando...' : 'Buscar'}
          </ActionButton>
        </Actions>
      </FiltrosHeader>

      <FiltrosGrid>
        {/* Busca geral */}
        <FiltroGroup>
          <Label>Busca Geral</Label>
          <Input
            type="text"
            placeholder="Nome, email ou telefone..."
            value={filtros.search}
            onChange={(e) => handleInputChange('search', e.target.value)}
          />
        </FiltroGroup>

        {/* Empresa */}
        <FiltroGroup>
          <Label>Empresa</Label>
          <Input
            type="text"
            placeholder="Nome da empresa..."
            value={filtros.empresa}
            onChange={(e) => handleInputChange('empresa', e.target.value)}
          />
        </FiltroGroup>

        {/* Segmento */}
        <FiltroGroup>
          <Label>Segmento</Label>
          <Select
            value={filtros.segmento}
            onChange={(e) => handleInputChange('segmento', e.target.value)}
          >
            <option value="">Todos os segmentos</option>
            {SEGMENTOS_OPTIONS.map((segmento) => (
              <option key={segmento.value} value={segmento.value}>
                {segmento.label}
              </option>
            ))}
          </Select>
        </FiltroGroup>

        {/* Funil */}
        <FiltroGroup>
          <Label>Funil</Label>
          <Select
            value={filtros.funil_id}
            onChange={(e) => handleInputChange('funil_id', e.target.value)}
          >
            <option value="">Todos os funis</option>
            {funisList.map((funil) => (
              <option key={funil.id} value={funil.id}>
                {funil.nome}
              </option>
            ))}
          </Select>
        </FiltroGroup>

        {/* Origem */}
        <FiltroGroup>
          <Label>Origem</Label>
          <Select
            value={filtros.origem_id}
            onChange={(e) => handleInputChange('origem_id', e.target.value)}
          >
            <option value="">Todas as origens</option>
            {origensList.map((origem) => (
              <option key={origem.id} value={origem.id}>
                {origem.nome}
              </option>
            ))}
          </Select>
        </FiltroGroup>
      </FiltrosGrid>
    </FiltrosContainer>
  );
};

export default LeadsFiltros;
